import { useEffect } from 'react';

import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useStoreRequired } from 'features/store/hooks/use-store-required';

import { CardModal } from 'components/card-modal';
import { useMaybeGetLegacyRedirect } from 'components/layout/use-maybe-get-legacy-redirect';
import { LocationEmptyState } from 'components/location-empty-state';
import { toaster } from 'components/toaster/toaster';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useAuthRequiredInModal } from 'pages/authentication/components/auth-required';
import IncentiveDetails from 'pages/loyalty/loyalty-incentives-components/incentive-details/incentive-details';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { t } from 'state/intl/translations';

export default function RewardDetail() {
  // lots of legacy redirects start with /rewards/[notReallyAnID] so we need this here and the 404 handler
  const redirectUrl = useMaybeGetLegacyRedirect(['rewardId']);

  useAuthRequiredInModal(!!redirectUrl); // handles deep link to reward w/o being logged in

  const router = useRouter();
  const { noStoreSelected } = useStoreRequired();

  const { rewardId } = useLocalSearchParams<{ rewardId: string }>();
  const { sanityRewardsMap, engineRewardsMap } = useLoyaltyRewardsList();

  const selectedReward = sanityRewardsMap?.[rewardId];
  const isLoading = !sanityRewardsMap || !engineRewardsMap;

  // close with error toast if incentive can't be found
  useEffect(() => {
    if (redirectUrl) {
      return;
    }

    if (!isLoading && !selectedReward) {
      if (!router.canDismiss()) {
        return; // nav is loading still on a deep link
      }
      router.dismiss();
      toaster.negative(t('nonAvailableReward'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, selectedReward, redirectUrl]);

  if (redirectUrl) {
    return <Redirect href={redirectUrl} />;
  }

  if (noStoreSelected) {
    return (
      <CardModal alwaysShowHeader>
        <LocationEmptyState title={t('rewards')} message={t('emptyMenuMessage')} />
      </CardModal>
    );
  }

  if (isLoading || !selectedReward) {
    return (
      <CardModal alwaysShowHeader isLoading disableGlobalAppModals>
        <LoyaltyLoader />
      </CardModal>
    );
  }

  return (
    <IncentiveDetails engineIncentivesMap={engineRewardsMap} selectedIncentive={selectedReward} />
  );
}
